/**
 * User Hooks
 *
 * React Query hooks for user operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api, { clearTokens } from '@/lib/api'
import type {
  User,
  UserCreate,
  UserUpdate,
  LoginRequest,
  LoginResponse,
  ChangePasswordRequest,
} from '@/types'

const USERS_KEY = 'users'

export function useUsers() {
  return useQuery({
    queryKey: [USERS_KEY],
    queryFn: async () => {
      const response = await api.get<User[]>('/users')
      return response.data
    },
  })
}

export function useUser(id: number) {
  return useQuery({
    queryKey: [USERS_KEY, id],
    queryFn: async () => {
      const response = await api.get<User>(`/users/${id}`)
      return response.data
    },
    enabled: !!id,
  })
}

export function useCurrentUser() {
  return useQuery({
    queryKey: [USERS_KEY, 'me'],
    queryFn: async () => {
      const response = await api.get<User>('/users/me')
      return response.data
    },
  })
}

export function useCreateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UserCreate) => {
      const response = await api.post<User>('/users', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_KEY] })
    },
  })
}

export function useUpdateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UserUpdate }) => {
      const response = await api.put<User>(`/users/${id}`, data)
      return response.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [USERS_KEY] })
      queryClient.invalidateQueries({ queryKey: [USERS_KEY, variables.id] })
    },
  })
}

export function useDeleteUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/users/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_KEY] })
    },
  })
}

export function useLogin() {
  return useMutation({
    mutationFn: async (data: LoginRequest) => {
      const formData = new URLSearchParams()
      formData.append('username', data.username)
      formData.append('password', data.password)

      const response = await api.post<LoginResponse>('/auth/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      return response.data
    },
    onSuccess: (data) => {
      localStorage.setItem('access_token', data.access_token)
    },
  })
}

export function useLogout() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      clearTokens()
    },
    onSuccess: () => {
      queryClient.clear()
    },
  })
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (data: ChangePasswordRequest) => {
      const response = await api.post('/users/me/change-password', data)
      return response.data
    },
  })
}
